import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, User, CheckCircle2, Loader2 } from "lucide-react";
import { useState } from "react";
import { usePartnerContext } from "@/hooks/usePartnerContext";
import { useContactRequests, type ContactType, type Urgency } from "@/hooks/useContactRequests";
import { toast } from "@/hooks/use-toast";

const CONTACT_TYPES: { value: ContactType; label: string }[] = [
  { value: "vaga", label: "Vaga de emprego" },
  { value: "entrevista_exploratoria", label: "Entrevista exploratória" },
  { value: "mentoria", label: "Mentoria" },
  { value: "networking", label: "Networking" },
  { value: "proposta", label: "Proposta" },
];

const URGENCY_LEVELS: { value: Urgency; label: string }[] = [
  { value: "baixa", label: "Baixa" },
  { value: "media", label: "Média" },
  { value: "alta", label: "Alta" },
];

export default function EmpresaContatoMediado() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const talentoId = searchParams.get("userId") ?? "";
  const talentoNome = searchParams.get("name") ?? "Talento";
  const { orgId } = usePartnerContext();
  const { createRequest } = useContactRequests(orgId);
  const [mensagem, setMensagem] = useState("");
  const [contactType, setContactType] = useState<ContactType>("vaga");
  const [urgency, setUrgency] = useState<Urgency>("media");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!mensagem.trim() || !talentoId) return;
    setIsSubmitting(true);
    try {
      await createRequest.mutateAsync({
        userId: talentoId,
        message: mensagem,
        contactType,
        urgency,
      });
      setSubmitted(true);
    } catch (err: any) {
      toast({ title: "Erro ao enviar", description: err.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="page-container-desktop max-w-lg mx-auto flex flex-col items-center justify-center min-h-[60vh]">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.6 }}
          className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-success" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Solicitação enviada!</h1>
          <p className="text-muted-foreground mb-2">A equipe Corre Digital vai mediar o contato com o talento.</p>
          <p className="text-sm text-muted-foreground mb-8">Você receberá uma notificação quando houver atualização.</p>
          <Button variant="default" size="lg" className="w-full" onClick={() => navigate("/empresa")}>Voltar para Vitrine</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="page-container-desktop max-w-lg mx-auto">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="w-5 h-5" /></Button>
        <span className="text-sm text-muted-foreground">Solicitar Contato Mediado</span>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl p-4 shadow-card border border-border mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <User className="w-6 h-6 text-primary" />
          </div>
          <div>
            <p className="font-medium text-foreground">{talentoNome}</p>
            <p className="text-xs text-muted-foreground">Todo contato passa pelo Corre Digital para proteção do talento.</p>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-5">
        {/* Contact Type */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Tipo de oportunidade</label>
          <Select value={contactType} onValueChange={(v) => setContactType(v as ContactType)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTACT_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Urgency */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Urgência</label>
          <Select value={urgency} onValueChange={(v) => setUrgency(v as Urgency)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {URGENCY_LEVELS.map((u) => (
                <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Message */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Mensagem para o talento</label>
          <Textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)}
            placeholder="Descreva a oportunidade e por que você gostaria de conversar com este talento..."
            className="min-h-[120px]" maxLength={1000} />
          <p className="text-xs text-muted-foreground text-right mt-1">{mensagem.length}/1000</p>
        </div>

        <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
          <div className="flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Como funciona?</p>
              <ol className="text-xs text-muted-foreground mt-1 space-y-1 list-decimal pl-4">
                <li>Você envia a solicitação</li>
                <li>Admin do Corre Digital aprova ou rejeita</li>
                <li>Se aprovado, o aluno decide se aceita o contato</li>
                <li>Só após dupla permissão o contato é liberado</li>
              </ol>
            </div>
          </div>
        </div>

        <Button variant="hero" size="xl" className="w-full" onClick={handleSubmit} disabled={!mensagem.trim() || isSubmitting}>
          {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" />Enviando...</>) : "Enviar Solicitação"}
        </Button>
      </motion.div>
    </div>
  );
}
